import React from 'react';
// import LazyLoad from "react-lazyload";
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import cx from 'classnames';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useDeviceWidth from 'utils/hooks/useDeviceWidth';
import breakpoints from 'utils/breakpoints';
import HeartFilled from 'components/icons/HeartFilled';
import HeartIcon from 'components/icons/Heart';
import useFavorites from 'utils/hooks/useFavorites';
import OpenNewTab from 'components/icons/OpenNewTab';
import { hasPrice } from 'utils/parsePrice';

import { colors } from 'utils/tailwindColors';

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.3);
`;

const Container = styled.div`
  max-width: 22.5rem;
`;
const CardBody = styled.div`
  ${({ isOnePerPage, mobileCardWidth }) =>
    isOnePerPage && mobileCardWidth && `min-width:${mobileCardWidth}px;`}

  height: 32rem;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0);
  transition: all 0.4s ease-out;
  @media ${breakpoints.sm} {
    min-width: 22.5rem;
  }

  ${({ isMobile }) =>
    isMobile
      ? boxShadow
      : `
      ${Container}:hover & {
        ${boxShadow}
        transform: translateY(-0.5rem);
      }`}
`;

const Img = styled.img`
  max-height: 32rem;
  transition: all 0.4s ease-out;

  ${Container}:hover & {
    max-height: 100vh;
  }
`;
const IconContainer = styled.div`
  min-width: 2.25rem;
  height: 2.25rem;
`;

const Overlay = styled.div`
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5956757703081232) 17%,
    rgba(255, 255, 255, 0) 87%
  );
`;

const Content = styled.div`
  max-height: 0;
  transition: all 0.4s ease-out;
  ${({ isMobile }) => isMobile && 'height: 100%;'}
  ${({ isMobile }) =>
    isMobile &&
    `
    max-height: 100%;
    height: auto;
  `}
  ${Container}:hover & {
    max-height: 100vh;
    height: auto;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
  color: white;
  background-color: ${colors['mattamy-blue-light']};
`;

const Card = ({
  card = {},
  type = 'home',
  handleFavoriteClick = () => {},
  ...rest
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);
  const favoritesList = rest.favoritesList;
  const isOnePerPage = ['xs'].includes(breakpoint);
  const deviceWidth = useDeviceWidth();
  const mobileCardWidth = deviceWidth && Math.round(deviceWidth * 0.8);

  const cta = Array.isArray(card.ctas) ? card.ctas[0] : null;

  const id = card.id;
  const isFavorite = favoritesList.includes(id);
  const hasStartingFromPrice = hasPrice(card.startingFrom);
  const priceText = hasStartingFromPrice
    ? `${card.startingFromLabel} ${card.startingFrom}`
    : card.noPriceText;

  const getMpcHomeTypes = (homeTypes) => {
    if (homeTypes) {
      let homeTypesList = homeTypes.split(', ');
      homeTypesList = homeTypesList.filter((e) => e);
      return [].map
        .call(homeTypesList, function (e) {
          return e;
        })
        .join(', ');
    }
    return '';
  };

  const getCommunityStylesFormat = (communityStyles) => {
    const styles = communityStyles.split(',');
    if (styles && styles.length > 1) {
      let newFormat = styles.reduce(function (a, b) {
        return a.concat(', ' + b);
      });
      return newFormat;
    }

    return communityStyles;
  };

  return (
    <Container {...rest}>
      <div>
        <CardBody
          className='relative w-full flex flex-col rounded overflow-hidden transform'
          isMobile={isMobile}
          isOnePerPage={isOnePerPage}
          mobileCardWidth={mobileCardWidth}
        >
          <div className='h-full'>
            <div className='h-full w-full relative z-10'>
              <a
                href={
                  (type === 'home' ||
                    type === 'community' ||
                    type === 'promo') &&
                  cta &&
                  cta.href
                }
                target={
                  ((type === 'home' ||
                    type === 'community' ||
                    type === 'promo') &&
                    cta &&
                    cta.target) ||
                  '_self'
                }
                aria-label={
                  card.title +
                  ' ' +
                  getMpcHomeTypes(card.subtitle) +
                  ' ' +
                  card.paragraph +
                  ' ' +
                  'View ' +
                  card.title +
                  ' information. ' +
                  (cta && cta.target && cta.target === '_blank'
                    ? '(Opens in new tab)'
                    : '')
                }
              >
                <div width={360}>
                  <Img
                    className='absolute left-0 top-0 object-cover h-full min-w-full'
                    src={card.imageUrl}
                    alt={card.imageAlt}
                  />
                </div>
              </a>
              {type === 'community' && (
                <>
                  <IconContainer
                    className='flex items-center absolute top-0 right-0 justify-center
                                rounded-full bg-white text-action-blue mt-10-mattamy mr-3 hover:bg-mattamy-blue
                                hover:text-white cursor-pointer'
                    onClick={() => handleFavoriteClick(id)}
                    role='button'
                    aria-label={
                      isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
                    }
                    aria-pressed={isFavorite ? 'true' : 'false'}
                  >
                    {isFavorite ? (
                      <HeartFilled className='h-5 w-5' />
                    ) : (
                      <HeartIcon className='h-5 w-5' />
                    )}
                  </IconContainer>
                  <div className='absolute top-0 left-0 mt-10-mattamy ml-3'>
                    <AvailableFlag
                      className='flex items-center justify-center
                                  px-6 py-1 mt-10-mattamy mr-3 md:mt-0 bg-white text-mattamy-blue-light
                                  hover:bg-mattamy-blue-light hover:text-white'
                    >
                      <p className='text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1'>
                        {card.bubbletext}
                      </p>
                    </AvailableFlag>
                  </div>
                </>
              )}
            </div>
          </div>
          <Overlay className='absolute bottom-0 w-full h-1/2 z-20'></Overlay>
          <div className='absolute bottom-0 left-0 ml-5 mb-4 z-30'>
            <p className='text-normal font-graphie text-white mb-1'>
              {getMpcHomeTypes(card.subtitle)}
            </p>
            <h2 className='font-trade-gothic-20 text-3xl text-white uppercase pr-24 mr-4'>
              {card.title}
            </h2>
          </div>
          <Content
            className={cx('z-30 bg-white', {
              'absolute bottom-0 left-0 right-0': isMobile,
            })}
            isMobile={isMobile}
          >
            <a
              href={
                (type === 'home' || type === 'community' || type === 'promo') &&
                cta &&
                cta.href
              }
              target={
                ((type === 'home' ||
                  type === 'community' ||
                  type === 'promo') &&
                  cta &&
                  cta.target) ||
                '_self'
              }
              aria-label={
                card.title +
                ' ' +
                getMpcHomeTypes(card.subtitle) +
                ' ' +
                card.paragraph +
                ' ' +
                'View ' +
                card.title +
                ' information. ' +
                (cta && cta.target && cta.target === '_blank'
                  ? '(Opens in new tab)'
                  : '')
              }
            >
              <div
                className={cx('', {
                  'pt-10-mattamy px-5 pb-4': type === 'home',
                  'py-8 px-5': type === 'community' || type === 'promo',
                })}
              >
                <p className='text-normal font-graphie text-mattamy-blue mb-1'>
                  {getMpcHomeTypes(card.subtitle)}
                </p>
                <h2 className='font-trade-gothic-20 text-3xl text-mattamy-blue uppercase lg:pr-24'>
                  {card.title}
                </h2>
                {type === 'community' && (
                  <p className='text-mattamy-gray-font text-lg font-trade-gothic-20 text-left pb-4 font-bold'>
                    {card.communityStyles
                      ? getCommunityStylesFormat(card.communityStyles)
                      : ''}
                  </p>
                )}
                <p className='text-mattamy-blue text-sm font-graphie text-left'>
                  {card.paragraph}
                </p>
              </div>
              <div className='flex items-center justify-center bg-action-blue w-full h-60-mattamy'>
                {type === 'community' || type === 'promo' ? (
                  <a
                    className='flex justify-center items-center w-full h-full text-center text-white font-trade-gothic-20 uppercase'
                    href={cta && cta.href}
                    target={(cta && cta.target) || '_self'}
                    aria-label={
                      priceText +
                      ' View ' +
                      card.title +
                      ' information' +
                      (cta && cta.target && cta.target === '_blank'
                        ? '(Opens in new tab)'
                        : '')
                    }
                  >
                    {type === 'promo' && cta && cta.text}
                    {priceText}

                    {cta && cta.target && cta.target === '_blank' && (
                      <OpenNewTab className='mb-1 ml-1 w-5' />
                    )}
                  </a>
                ) : (
                  <div className='flex justify-center items-center w-full h-full text-center text-white font-trade-gothic-20 uppercase'>
                    {cta && cta.text}
                  </div>
                )}
              </div>
            </a>
          </Content>
        </CardBody>
      </div>
    </Container>
  );
};

Card.propTypes = {
  card: PropTypes.object,
};

export default Card;
